/* eslint-disable no-console, no-undef */
import React, { useCallback, useRef, useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Card, Tag, Spin, Switch } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { usePostMonitoringCommandMutation } from "../../../redux/monitoring/monitoringAPI";
import { updateSendCommand } from "../../../redux/monitoring/monitoringSlice";
// FLAT BUFFERS
import { decodeUiMessage } from "../../../flatbuffers/decoders/uiMessageDecoder";
import { createUiCmdPayload } from "../../../flatbuffers/builders/uiCmdBuilder";
// MONITORING STATE
import { useBankDispatch } from "../socketMessageState/BankMessagesContext";
// SOCKET EMITTER
import socketEmitter from "./MonitoringWebSocketEmitter";
// AUTHORIZATION
import GETJwtTokenSocket from "../../../redux/authentication/authenticationSocket";
import GETJwtToken from "../../../redux/authentication/authentication";
// CUSTOM HOOKS
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// INTERFACES
import { RootState } from "../../../app/store";
import { LayoutInterface } from "../../../interfaces/Monitoring.interface";
import { MessageInterface } from "../../../interfaces/Message.interface";

const FAULT_PERSISTENCE_KEY_PREFIX = "liftnet_expired_faults_";

// Helper to create composite fault key
const createFaultKey = (bankId: string, liftId: string, fault: string): string => `${bankId}:${liftId}:${fault}`;

// Helper to create fault key prefix for a bank/lift combination
const createFaultKeyPrefix = (bankId: string, liftId: string): string => `${bankId}:${liftId}:`;

// LocalStorage helpers for fault persistence
const getPersistedFaults = (bankId: string): Set<string> => {
	const key = `${FAULT_PERSISTENCE_KEY_PREFIX}${bankId}`;
	try {
		const stored = localStorage.getItem(key);
		if (stored) {
			return new Set(JSON.parse(stored));
		}
	} catch (e) {
		console.error("❌ [FAULT_TIMER] Failed to load persisted faults:", e);
	}
	return new Set();
};

const savePersistedFaults = (bankId: string, faults: Set<string>) => {
	const key = `${FAULT_PERSISTENCE_KEY_PREFIX}${bankId}`;
	try {
		if (faults.size > 0) {
			localStorage.setItem(key, JSON.stringify(Array.from(faults)));
		} else {
			localStorage.removeItem(key);
		}
	} catch (e) {
		console.error("❌ [FAULT_TIMER] Failed to save persisted faults:", e);
	}
};

function MonitoringWebSocketConnection({
	serverId,
	serverName,
	channelType,
	liftName,
	liftId,
	channelId,
	layouts,
}: {
	serverId: string;
	serverName: string;
	channelType: string;
	liftName: string;
	liftId: string;
	channelId: string;
	layouts: LayoutInterface[];
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const socket = useRef<WebSocket | null>(null);
	const SOCKET_URL = process.env.REACT_APP_WEB_SOCKET_EDGE_SERVER;
	const myIntervalCheckSocketConnection: any = useRef(null);
	const CONNECTING = 0;
	const OPEN = 1;
	const CLOSING = 2;
	const CLOSED = 3;
	const dispatch = useBankDispatch();
	const dispatchRedux = useDispatch();

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [enabledAutoRefresh, setEnabledAutoRefresh] = useState<boolean>(true);
	const [enabledSendPing, setEnabledSendPing] = useState<boolean>(true);

	// ************************************************ */
	// FAULT TIMER STATE ****************************** */
	// eslint-disable-next-line no-undef
	const activeFaults = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
	const expiredFaults = useRef<Set<string>>(new Set());
	const persistedFaultsByBank = useRef<Map<string, Set<string>>>(new Map());
	const messageDebounceMap = useRef<Map<string, number>>(new Map());
	const lastMessagesByLift = useRef<Map<string, any>>(new Map());

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { showQueueInfo, stopSocketConnection, sendCommand } = useSelector((state: RootState) => state.monitoring);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [postCommand, { isLoading: isLoadingPostCommand }] = usePostMonitoringCommandMutation();
	const { messages } = useGetAllMessages({ deviceType: "", messageTypeId: "" });

	// ************************************************ */
	// FUNCTIONS ************************************** */

	const getDebounceTime = useCallback((fault: string): number => messageDebounceMap.current.get(fault) ?? 0, []);

	const shouldFaultBlink = useCallback((faultStrings: string[], bankId: string, targetLiftId: string): boolean => {
		const persistedFaults = persistedFaultsByBank.current.get(bankId) || new Set();
		const faultKeyPrefix = createFaultKeyPrefix(bankId, targetLiftId);
		const allExpiredFaults = Array.from(expiredFaults.current);
		const hasExpiredFaultsForThisLift = allExpiredFaults.some((key) => key.startsWith(faultKeyPrefix));
		const matchesPersistedFault = faultStrings.some((fault) => persistedFaults.has(fault));

		return hasExpiredFaultsForThisLift || matchesPersistedFault;
	}, []);

	const manageFaultTimers = useCallback(
		(faultsWithDebounce: Array<{ fault: string; debounceMs: number }>, bankId: string, targetLiftId: string) => {
			const incomingFaultSet = new Set(faultsWithDebounce.map((f) => f.fault));
			const currentFaults = activeFaults.current;
			const faultKeyPrefix = createFaultKeyPrefix(bankId, targetLiftId);

			const triggerImmediateUpdate = () => {
				const key = createFaultKeyPrefix(bankId, targetLiftId);
				const lastMsg = lastMessagesByLift.current.get(key);
				if (lastMsg) {
					const shouldBlink = shouldFaultBlink(lastMsg.fault_strings, bankId, targetLiftId);
					dispatch({
						type: "UPDATE_MESSAGE",
						payload: { ...lastMsg, faults: shouldBlink },
					});
				}
			};

			const updatePersistence = (faultStr: string, shouldAdd: boolean) => {
				try {
					const currentSet = persistedFaultsByBank.current.get(bankId) || new Set();
					if (shouldAdd) currentSet.add(faultStr);
					else currentSet.delete(faultStr);
					persistedFaultsByBank.current.set(bankId, currentSet);
					savePersistedFaults(bankId, currentSet);
				} catch (e) {
					console.error("Error updating fault persistence:", e);
				}
			};

			// 1. START TIMERS
			faultsWithDebounce.forEach(({ fault, debounceMs }) => {
				const faultKey = createFaultKey(bankId, targetLiftId, fault);
				if (!currentFaults.has(faultKey) && !expiredFaults.current.has(faultKey)) {
					if (debounceMs === 0) {
						expiredFaults.current.add(faultKey);
						updatePersistence(fault, true);
						triggerImmediateUpdate();
					} else {
						const timer = setTimeout(() => {
							expiredFaults.current.add(faultKey);
							currentFaults.delete(faultKey);
							updatePersistence(fault, true);
							triggerImmediateUpdate();
						}, debounceMs);
						currentFaults.set(faultKey, timer);
					}
				}
			});

			// 2. CANCEL TIMERS
			Array.from(currentFaults.keys()).forEach((faultKey) => {
				if (faultKey.startsWith(faultKeyPrefix)) {
					const fault = faultKey.substring(faultKeyPrefix.length);
					if (!incomingFaultSet.has(fault)) {
						const timer = currentFaults.get(faultKey);
						if (timer) clearTimeout(timer);
						currentFaults.delete(faultKey);
						updatePersistence(fault, false);
					}
				}
			});

			// 3. STOP BLINKING
			Array.from(expiredFaults.current).forEach((faultKey) => {
				if (faultKey.startsWith(faultKeyPrefix)) {
					const fault = faultKey.substring(faultKeyPrefix.length);
					if (!incomingFaultSet.has(fault)) {
						expiredFaults.current.delete(faultKey);
						updatePersistence(fault, false);
						triggerImmediateUpdate();
					}
				}
			});
		},
		[dispatch, shouldFaultBlink],
	);

	const onMessageFlatBuffer = useCallback(
		async (socketMessage: ArrayBuffer | Blob | string) => {
			if (typeof socketMessage === "string") {
				console.error("❌ WS sent STRING instead of binary:", socketMessage);
				return;
			}
			let buffer: ArrayBuffer;
			if (socketMessage instanceof Blob) {
				buffer = await socketMessage.arrayBuffer();
			} else {
				buffer = socketMessage;
			}

			const bytes = new Uint8Array(buffer);
			try {
				const msg = decodeUiMessage(bytes);
				const faultStrings = msg?.record?.faults ?? [];
				const bankId = msg?.record?.bank_id ?? "";
				const msgLiftId = msg?.record?.lift_id ?? "";

				const faultsWithDebounce = faultStrings.map((fault) => ({
					fault,
					debounceMs: getDebounceTime(fault),
				}));
				manageFaultTimers(faultsWithDebounce, bankId, msgLiftId);

				const shouldBlink = shouldFaultBlink(faultStrings, bankId, msgLiftId);

				const payloadData = {
					bank_id: bankId,
					lift_id: msg?.record?.lift_id,
					car_calls: msg?.record?.car_calls,
					hall_calls: msg?.record?.hall_calls,
					faults: shouldBlink,
					fault_strings: faultStrings,
					position: msg?.record?.position,
					service_mode: msg?.record?.service_mode?.value ?? "",
					direction: msg?.record?.direction,
					door_status: msg?.record?.door_status,
					connected: !(
						(msg?.record?.msg?.content?.message?.payload?.message?.value === "Connected" &&
							!msg?.record?.msg?.active) ||
						socket.current?.readyState === WebSocket.CLOSED
					),
					msg: {
						type: msg?.record?.msg?.content?.type ?? "Status",
						active: msg?.record?.msg?.active ?? false,
						value:
							msg?.record?.msg?.content?.message?.value ??
							msg?.record?.msg?.content?.message?.message?.value ??
							msg?.record?.msg?.content?.message?.payload?.message?.value ??
							"",
					},
				};

				// Save to ref for timer-triggered updates
				const liftKey = createFaultKeyPrefix(bankId, msgLiftId);
				lastMessagesByLift.current.set(liftKey, payloadData);

				dispatch({
					type: "UPDATE_MESSAGE",
					payload: payloadData,
				});
			} catch (err) {
				console.error("❌ Decoder FAILED", err);
			}
		},
		[manageFaultTimers, shouldFaultBlink, getDebounceTime, dispatch],
	);

	const refreshAll = useCallback(async () => {
		const token = await GETJwtToken();
		const BODY = {
			token,
			body: { lift_name: liftName, lift_id: liftId, channel_id: "", key_id: "", target_value: 0 },
			serverId,
		};
		postCommand(BODY);
	}, [liftName, liftId, serverId, postCommand]);

	const onSocketOpen = useCallback(() => {
		if (channelType !== "ADAPTER") refreshAll();
	}, [channelType, refreshAll]);

	const onSocketClose = useCallback(() => {}, []);

	const onConnect = useCallback(async () => {
		if (socket.current?.readyState !== WebSocket.OPEN) {
			const token = await GETJwtTokenSocket();
			socket.current = new WebSocket(`${SOCKET_URL}?serverID=${serverId}&access_token=${token}`);
			socket.current.addEventListener("open", onSocketOpen);
			socket.current.addEventListener("close", onSocketClose);
			socket.current.addEventListener("message", (event: any) => {
				onMessageFlatBuffer(event.data);
			});
		}
	}, [serverId, SOCKET_URL, onMessageFlatBuffer, onSocketOpen, onSocketClose]);

	const onDisconnect = useCallback(() => {
		socket.current?.close();
	}, []);

	const checkSocketConnection = useCallback(() => {
		if (socket.current?.readyState === CLOSED && enabledAutoRefresh) {
			onConnect();
		}
	}, [enabledAutoRefresh, onConnect, CLOSED]);

	const onStartIntervalCheckSocketConnection = useCallback(() => {
		myIntervalCheckSocketConnection.current = setInterval(() => {
			checkSocketConnection();
		}, 30 * 1000);
	}, [checkSocketConnection]);

	useEffect(() => {
		if (messages && messages.length > 0) {
			const map = new Map<string, number>();
			messages.forEach((msg: MessageInterface) => {
				map.set(msg.short_message, msg.debounce_time ?? 0);
			});
			messageDebounceMap.current = map;
		}
	}, [messages]);

	useEffect(() => {
		layouts.forEach((layout) => {
			const persisted = getPersistedFaults(layout.idBank);
			if (persisted.size > 0) {
				persistedFaultsByBank.current.set(layout.idBank, persisted);
			}
		});

		if (serverId) {
			onConnect();
			onStartIntervalCheckSocketConnection();
		}
		return () => {
			socketEmitter.removeAllListeners("sendCommand");
			socket.current?.close();
			if (myIntervalCheckSocketConnection.current) clearInterval(myIntervalCheckSocketConnection.current);
			activeFaults.current.forEach((timer) => clearTimeout(timer));
			activeFaults.current.clear();
			expiredFaults.current.clear();
		};
	}, [serverId, onConnect, onStartIntervalCheckSocketConnection, layouts]);

	useEffect(() => {
		if (stopSocketConnection) onDisconnect();
		else onConnect();
	}, [stopSocketConnection, onConnect, onDisconnect]);

	useEffect(() => {
		if (sendCommand.sendCommand && socket.current?.readyState === WebSocket.OPEN) {
			const payload = createUiCmdPayload({
				message: sendCommand.body.command,
				position: sendCommand.body.position,
				active: sendCommand.body.active,
				channelId,
				bankId: sendCommand.body.bankId,
				liftId: sendCommand.body.liftId,
			});
			console.info({ payload });
			console.info({
				message: sendCommand.body.command,
				position: sendCommand.body.position,
				active: sendCommand.body.active,
				channelId,
				bankId: sendCommand.body.bankId,
				liftId: sendCommand.body.liftId,
			});
			socket.current.send(payload);
			dispatchRedux(updateSendCommand({ sendCommand: false, serverId: "", body: {} }));
		}
	}, [sendCommand, channelId, dispatchRedux]);

	return (
		<Spin spinning={isLoadingPostCommand}>
			{showQueueInfo && (
				<div>
					<Card
						title={serverName}
						style={{ width: 550, marginLeft: "15px", marginTop: "10px" }}
						extra={
							<div>
								{socket.current?.readyState !== OPEN && <Button onClick={onConnect}>Connect</Button>}
								{socket.current?.readyState === OPEN && <Button onClick={onDisconnect}>Disconnect</Button>}
								<Switch
									onChange={setEnabledAutoRefresh}
									checked={enabledAutoRefresh}
									checkedChildren='Reconnection'
									unCheckedChildren='Reconnection'
									style={{ marginLeft: "10px" }}
								/>
								<Switch
									onChange={setEnabledSendPing}
									checked={enabledSendPing}
									checkedChildren='Ping'
									unCheckedChildren='Ping'
									style={{ marginLeft: "10px" }}
								/>
							</div>
						}
					>
						{/* Summary Info */}
						<p style={{ marginBottom: "-1px" }}>
							<b className={socket.current?.readyState === OPEN ? "generalStyles__info" : "generalStyles__warning"}>
								Status:
							</b>{" "}
							<b style={{ color: "white" }}>
								{socket.current?.readyState === OPEN && <Tag color='green'>OPEN</Tag>}
								{socket.current?.readyState === CONNECTING && <Tag color='cyan'>CONNECTING</Tag>}
								{socket.current?.readyState === CLOSING && <Tag color='orange'>CLOSING</Tag>}
								{socket.current?.readyState === CLOSED && <Tag color='red'>CLOSED</Tag>}
							</b>
						</p>
						<p style={{ marginBottom: "-1px" }}>
							<b>Socket Connection:</b> <span>EDGE (ONLINE)</span>
						</p>
						<p style={{ marginBottom: "-1px" }}>
							<b>Server:</b> <span>{serverId}</span>
						</p>
						<p style={{ marginBottom: "-1px" }}>
							<b>Channel Type:</b> <span>{channelType}</span>
						</p>
					</Card>
				</div>
			)}
		</Spin>
	);
}

export default MonitoringWebSocketConnection;
